// Reference: https://github.com/adobe/react-spectrum/blob/f8e9a4519cbdd82f510f5034af0705fa6a6325dd/packages/react-aria/src/overlays/usePreventScroll.ts

import { useLayoutEffect, type HTMLInputTypeAttribute } from "react";

import { platformApi } from "@/app/platform";

let preventScrollCount = 0;
let restore: () => void = () => {};

type UseHandleInputFocusOptions = {
  isDisabled?: boolean;
};

/**
 * Fixes issues with fixed positioned elements being pushed out of view when the on-screen keyboard opens on iOS.
 *
 * Issue overview:
 * On iOS, focusing an input inside a drawer or modal causes the whole page to
 * scroll. The fix is to call `focus({ preventScroll: true })` to suppress the browser's automatic scroll,
 * then manually scroll the target into view once the keyboard is already open.
 *
 * This must happen just before the browser would focus the element itself — the
 * `blur` event on the previously focused element fires at exactly that moment,
 * which is why `preventScrollMobileSafari` listens on `blur`.
 */
export function useHandleInputFocus(
  options: UseHandleInputFocusOptions = {},
): void {
  const { isDisabled } = options;

  useLayoutEffect(() => {
    if (isDisabled) {
      return;
    }

    preventScrollCount++;
    if (preventScrollCount === 1) {
      if (platformApi.deviceType === "iOS") {
        restore = preventScrollMobileSafari();
      }
    }

    return () => {
      preventScrollCount--;
      if (preventScrollCount === 0) {
        restore();
      }
    };
  }, [isDisabled]);
}

function preventScrollMobileSafari() {
  const onBlur = (e: FocusEvent) => {
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    const wasKeyboardOpen = willOpenKeyboard(target);
    const isKeyboardOpenNext =
      !!relatedTarget && willOpenKeyboard(relatedTarget);

    if (isKeyboardOpenNext) {
      relatedTarget.focus({ preventScroll: true });
      scrollIntoViewWhenReady(relatedTarget, wasKeyboardOpen);
    }
  };

  document.addEventListener("blur", onBlur, true);

  return () => {
    document.removeEventListener("blur", onBlur, true);
  };
}

// HTML input types that do not cause the keyboard to appear.
const nonTextInputTypes: Set<HTMLInputTypeAttribute> = new Set([
  "checkbox",
  "radio",
  "range",
  "color",
  "file",
  "image",
  "button",
  "submit",
  "reset",
]);

function willOpenKeyboard(target: Element) {
  return (
    (target instanceof HTMLInputElement &&
      !nonTextInputTypes.has(target.type)) ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function scrollIntoViewWhenReady(target: Element, wasKeyboardVisible: boolean) {
  // If the keyboard was already visible, scroll the target into view immediately.
  if (wasKeyboardVisible || !window.visualViewport) {
    scrollIntoView(target);

    return;
  }

  // Otherwise, wait for the visual viewport to resize before scrolling so we can
  // measure the correct position to scroll to.
  window.visualViewport.addEventListener(
    "resize",
    () => scrollIntoView(target),
    {
      once: true,
    },
  );
}

function scrollIntoView(target: Element) {
  target.scrollIntoView({ block: "center", behavior: "smooth" });
}
